import { LitElement, html, css, unsafeCSS } from 'lit';
import styles from './sku-list.scss?inline';
import { SELECTED_PROPERTY_POSITIONS, SHOW_MORE_CLICK_EVENT } from '../contants';
import throttle from 'lodash/throttle';
import { hideAndGetOverflowItems, resetVisibility } from '@/helpers/moveHiddenToDeopdown';

export default class SotbitPropertySkuList extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        text: { type: String },
        skuAttribute: { type: String, attribute: 'sku-attribute' },
        propertyTitle: { type: String, attribute: 'property-title' },
        propertyValue: { type: String, attribute: 'property-value' },
        propertyInDropdown: { type: Boolean, attribute: 'property-in-dropdown' },
        useDropdown: { type: Boolean, attribute: 'use-dropdown' },
        open: { type: Boolean },
        selectedPropertyPosition: { type: String, attribute: 'selected-property-position' },
        hiddenCount: { type: Number, attribute: false },
    };

    constructor() {
        super();

        this.selectedPropertyPosition = null;
        this.skuAttribute = 'data-treevalue';
        this.propertyTitle = '';
        this.propertyValue = '';
        this.hiddenCount = 0;
        this.propertyInDropdown = false;
        this.useDropdown = false;
        this._adjacent = null;

        this._dropdown = null;
        this.ignoreCheckDropdown = false;

        this._handleSelectBind = this._handleSelect.bind(this);
        this._handleSlotChangeBind = this._handleSlotChange.bind(this);
        this._checkNeedDropdownThrottle = throttle(this._checkNeedDropdown.bind(this), 60);
    }

    setAdjacent(adjacent) {
        this._adjacent = adjacent;

        const skus = [...this.children];

        let propertyInDropdown = false;
        for (const sku of skus) {
            const adjacentSku = this.getAdjacentSku(sku);

            if (adjacentSku) {
                this.selectSku(adjacentSku, true);

                if (this.useDropdown) {
                    adjacentSku?.toggleAttribute('laptop-hidden', !sku.hasAttribute('hidden'));
                }
            }

            if (sku.hasAttribute('selected') && sku.hasAttribute('hidden')) {
                propertyInDropdown = true;
            }
        }

        if (propertyInDropdown !== this.propertyInDropdown) {
            this.propertyInDropdown = propertyInDropdown;
        }
    }

    getAdjacentSku(sku) {
        const targetAttribute = sku.getAttribute(this.skuAttribute);
        return this._adjacent?.querySelector(`[${this.skuAttribute}="${targetAttribute}"]`);
    }

    connectedCallback() {
        super.connectedCallback();
        this._setupResizeObserver();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._cleanupResizeObserver();
    }

    _setupResizeObserver() {
        this._cleanupResizeObserver();

        if (this.useDropdown) {
            this._resizeObserver = new ResizeObserver(this._checkNeedDropdownThrottle);
            this._resizeObserver.observe(this);
        }
    }

    _cleanupResizeObserver() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
    }

    _isNeedDropdown() {
        return this.scrollWidth > this.clientWidth;
    }

    _checkNeedDropdown() {
        this._resetSkuVisibility();
        this.propertyInDropdown = false;
        let hiddenCount = 0;
        const dropdownItems = hideAndGetOverflowItems(this, { cloneItems: false }, ({ node, needHide }) => {
            let adjacentTarget = null;
            if (this._adjacent) {
                adjacentTarget = this.getAdjacentSku(node);
            }

            adjacentTarget?.toggleAttribute('laptop-hidden', !needHide);

            if (needHide) {
                hiddenCount++;
            }

            if (needHide && node.hasAttribute('selected')) {
                this.propertyInDropdown = true;
            }
        });

        if (dropdownItems) {
            this.hiddenCount = hiddenCount;
        }
    }

    _handleSelect({ target }) {
        if (!target.hasAttribute(this.skuAttribute)) {
            target = target.closest(`[${this.skuAttribute}]`);

            if (!target) {
                return;
            }
        }

        if (target.hasAttribute('disabled')) {
            return;
        }

        this.selectSku(target);

        this.dispatchEvent(
            new CustomEvent('select', {
                bubbles: true,
                composed: true,
                detail: {
                    sku: target,
                },
            })
        );
    }

    unselectSkus(setAdjacent = true) {
        const skus = [...this.children];

        for (const sku of skus) {
            sku.removeAttribute('selected');
        }

        if (setAdjacent) {
            this._adjacent?.unselectSkus(false);
        }
    }

    selectSku(selectSku, setAdjacent = true) {
        this.unselectSkus(setAdjacent);

        const targetAttribute = selectSku.getAttribute(this.skuAttribute);
        const skus = [...this.children];

        for (const sku of skus) {
            if (sku.getAttribute(this.skuAttribute) === targetAttribute) {
                sku.setAttribute('selected', '');
                this.propertyValue = sku.text || '';
                this.propertyInDropdown = sku.hasAttribute('hidden');
                break;
            }
        }

        if (setAdjacent) {
            if (this._adjacent) {
                const adjacentTarget = this.getAdjacentSku(selectSku);
                if (adjacentTarget) {
                    this._adjacent?.selectSku(adjacentTarget, false);
                }
            }
        }
    }

    _resetSkuVisibility() {
        resetVisibility(this, (node) => {
            node.removeAttribute('laptop-hidden');
        });

        this.hiddenCount = 0;
    }

    _handleSlotChange() {
        if (!this._slotChangeThrottled) {
            this._slotChangeThrottled = throttle(() => {
                this._checkNeedDropdown();
            }, 60);
        }

        this._slotChangeThrottled();
    }

    _handleShowMoreClick() {
        if (this.hiddenCount <= 0) {
            return;
        }

        this.dispatchEvent(
            new CustomEvent(SHOW_MORE_CLICK_EVENT, {
                bubbles: true,
                composed: true,
            })
        );
    }

    render() {
        const showTopProperty = this.selectedPropertyPosition === SELECTED_PROPERTY_POSITIONS.TOP;
        const showBottomProperty = this.selectedPropertyPosition === SELECTED_PROPERTY_POSITIONS.BOTTOM;
        const showDropdown = this.hiddenCount > 0;

        return html`
            <div class="sku-list-wrapper" part="sku-list-wrapper">
                ${showTopProperty
                    ? html`
                          <sotbit-property class="property-top" id="property" text-nowrap part="property-top">
                              <span slot="title">${this.propertyTitle}</span>
                              <span slot="value">${this.propertyValue}</span>
                          </sotbit-property>
                      `
                    : ''}

                <div class="sku-list" part="sku-list">
                    <slot @slotchange="${this._handleSlotChange}" name="line" @click="${this._handleSelect}"></slot>
                    ${this.useDropdown
                        ? html`
                              <sotbit-property-sku
                                  @click="${this._handleShowMoreClick}"
                                  part="sku-list-counter"
                                  class="sku-list-counter ${!showDropdown ? 'hidden' : ''}"
                                  data-skip-hide
                                  slot="anchor"
                                  ?selected="${this.propertyInDropdown}"
                                  counter
                                  text="${this.hiddenCount}+"
                              ></sotbit-property-sku>
                          `
                        : ''}
                </div>

                ${showBottomProperty
                    ? html`
                          <sotbit-property
                              class="property-bottom"
                              part="property-bottom"
                              id="property"
                              text-nowrap
                              size="xs"
                          >
                              <span slot="title">${this.propertyTitle}</span>
                              <span slot="value">${this.propertyValue}</span>
                          </sotbit-property>
                      `
                    : ''}
            </div>
        `;
    }
}
